import { Tooltip } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AddMemberToSample from '@components/assign-team-member/AddMemberToSample';
import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import SampleComment from '@components/SampleComment';
import { toastError, toastSuccess } from '@components/Toast';
import TopDetails from '@components/TopDetails';

import useWindowSize from '@hooks/useWindowSize';

import {
	allFilterPages,
	generateSampleDetailsParams,
	sampleDetailsFilterFields
} from '@features/filter/static/filterUtil';
import SampleDetailsTopHeader from '@features/filter/v2/core/Sample/SampleDetailsTopHeader';
import FilterComponent from '@features/filter/v2/FilterComponent';
import RequestTimeline from '@features/sampleDevelopment/components/RequestTimeline';
import SampleActionPopup from '@features/sampleDevelopment/components/SampleActionPopup';
import SampleDesignCard from '@features/sampleDevelopment/components/SampleDesignCard';
import SampleInlineComment from '@features/sampleDevelopment/components/SampleInlineComment';
import TableBodyForPatternMaster from '@features/sampleDevelopment/components/TableBodyForPatternMaster';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { handleFilterOfListPage } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchSupplierByMaterialType } from '@services/supplierService';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	checkPrimaryUserType,
	getActionDoneBy,
	getColorWiseActivityStatusOfSample,
	getShortName,
	getUserInfo,
	isPatternMaster,
	isSampleOrProductHead,
	packTag,
	renderProductCreator,
	setTag,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

import { BASE_URL, LIST_VIEW_TYPE } from '@/constants/constant';

const SampleViewForSampleDepartment = () => {
	const [sampleInfo, setSampleInfo] = useState({});
	const [listOfSample, setListOfSample] = useState([]);
	const [showComment, setShowComment] = useState(false);
	const [designIdList, setDesignIdList] = useState([]);
	const [activeRemarksId, setActiveRemarksId] = useState(null);
	const [designIdToView, setDesignIdToView] = useState(null);
	const [countryList, setCountryList] = useState([]);
	const [defaultViewType, setDefaultViewType] = useState(
		getUserInfo().systemPreference?.sampleDetailsViewType
	);
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [selectedSampleItemId, setSelectedSampleItemId] = useState(null);
	const [supplierList, setSupplierList] = useState([]);
	const [selectedRow, setSelectedRow] = useState(null);
	const [isUpdateClick, setIsUpdateClick] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const [isShowFilter, setIsShowFilter] = useState(true);
	const sampleViewStore = useSelector((store) => store.filterResponseStore);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const location = useLocation();
	const elementRef = useRef(null);
	const isDisabledSelect = ['BD-Inhouse', 'Turkey-Inhouse']?.includes(selectedRow?.unitResponse?.name);
	const isLoading = useSelector((store) => store.loaderStore);
	useWindowSize(() => setIsShowFilter(false));

	const getSetMembersIds = (memberOfSet = []) => {
		const membersList =
			memberOfSet.setMemberSampleRequestItemResponseList &&
			memberOfSet.setMemberSampleRequestItemResponseList.map((member) => member.id);
		return membersList;
	};

	const openDesignViewModal = (selectedDesign, designIdToView) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack: selectedDesign.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: designIdToView,
				onCloseViewModal: () => {
					setDesignIdToView(null);
					dispatch(clearDesignViewModalInfo({}));
				},
				updateProductStatus: () => false,
				onUpdateCostingStatus: () => false,
				source: 'PRODUCT_LIST',
				designIdList: designIdList
			})
		);
	};

	const fetchCountryList = () => {
		ApiClient.GET('getCountryListByUnits', 'SAMPLE_HOUSE')
			.then(({ data }) => {
				let lists = [];
				data.map((item) => lists.push({ id: item.id, value: item.name }));
				setCountryList(lists);
			})
			.catch(() => {
				toastError('Unable to fetch Country list!');
			});
	};

	const fetchSampleDetails = () => {
		const { ...paramsData } = generateSampleDetailsParams(filterfields);
		dispatch(handleFilterOfListPage({ type: 'sampleDetailsAction', idUrl: `${id}` }, paramsData, false));
	};

	useEffect(() => {
		let params = new URLSearchParams(location.search);
		let selectedSample = params.get('selectedSample');
		if (selectedSample) {
			setSelectedSampleItemId(selectedSample);
		}
		if (parseInt(params.get('sampleRequestId')) === parseInt(id)) {
			setSelectedId(params.get('productId'));
			setShowComment(true);
		}
	}, [location]);

	useEffect(() => {
		fetchCountryList();
		return () => {
			dispatch(clearCurrentActivePage());
		};
	}, []);

	useEffect(() => {
		if (sampleViewStore?.sampleRequestItemResponseList) {
			const designList = sampleViewStore.sampleRequestItemResponseList
				.filter((item) => item.productId)
				.map((item) => item.productId);

			setDesignIdList(designList);
			setSampleInfo(sampleViewStore);
			setListOfSample(sampleViewStore.sampleRequestItemResponseList);
		}
	}, [sampleViewStore]);

	const checkSingular = (title, value = 0) => {
		if (parseInt(value) > 1) {
			return `${value + ' ' + title}s`;
		}
		return value + ' ' + title;
	};

	const handleComplete = (type, sampleItemId) => {
		dispatch(openLoader());
		let postData = {
			sampleActivityType: type.toUpperCase(),
			sampleItemId: sampleItemId
		};
		ApiClient.POST(`${BASE_URL}/sample/request/${id}/complete-activity`, postData, '', true)
			.then(() => {
				fetchSampleDetails();
				dispatch(closeLoader());
			})
			.catch(() => {
				toastError('Unable to fetch user list!');
				dispatch(closeLoader());
			});
	};

	const onChangeSubmissionDate = (e, selectedItem) => {
		let inputValue = e.target.value;
		setSelectedRow({ ...selectedRow, submissionPlanDate: inputValue });
		setSampleInfo((prev) => ({
			...prev,
			sampleRequestItemResponseList: prev.sampleRequestItemResponseList.map((item) => {
				if (item.id === selectedItem.id) {
					return {
						...item,
						submissionPlanDate: inputValue
					};
				}
				return item;
			})
		}));
		handleSubmit({ ...selectedRow, submissionPlanDate: inputValue });
	};

	const handleSubmit = (data) => {
		const ids = getSetMembersIds(data);
		dispatch(openLoader());
		let postData = {
			id: data?.isSetMember ? data.setParentId : data.id,
			operationalUnitId: data?.unitResponse?.id,
			developmentFactoryId: data?.developmentFactory?.id || null,
			fabricSupplierId: data?.fabricSupplier?.id || null,
			setMemberIdList: ids,
			submissionPlanDate: data?.submissionPlanDate,
			isSet: data.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
		};
		ApiClient.PUT(`${BASE_URL}/sample/request/${id}/items`, postData, '', true)
			.then(({ data }) => {
				toastSuccess(data.message);
				setSelectedRow(null);
				setIsUpdateClick(false);
				fetchSampleDetails();
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const renderTopBar = () => {
		return (
			<>
				<div className="top-header transparent-filter">
					<div className="filter-container-top pt-2 pb-0 bg-gray">
						<div className="sample-full-status">
							<ul className="common-list-component">
								<li>
									<img
										src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
										alt="back"
										onClick={() => {
											navigate(-1);
										}}
										className="cursor-pointer"
									/>
									<span className="semibold-14 ml-2">
										{sampleInfo?.title || sampleInfo?.collectionName}
									</span>
									{sampleInfo?.refNo && (
										<span className="regular-12 gray_dark_02 weight-400">
											({sampleInfo?.refNo})
										</span>
									)}
								</li>

								{sampleInfo?.requestedBy && (
									<li>
										<span className="gray_dark_01">{sampleInfo?.requestedBy}</span>
									</li>
								)}
								{sampleInfo?.deliveryDate && (
									<li>
										<span>
											{changeDateFormat(
												sampleInfo?.deliveryDate,
												'YYYY-MM-DD',
												'DD MMM'
											)}
										</span>
									</li>
								)}
								<li>
									<span className="gray_dark_01">
										{checkSingular('Design', sampleInfo?.noOfDesign)}{' '}
									</span>
								</li>
								<li>
									<span>
										<span className="brand-common-design">{sampleInfo?.brand}</span>
									</span>
								</li>
							</ul>
						</div>
						<div className="filter-actions sample-dashboard-filter">
							<AddMemberToSample sampleId={id} />
						</div>
					</div>
					<SampleDetailsTopHeader
						toggleFilter={onCollspseFilter}
						setDefaultViewType={setDefaultViewType}
						elementRef={elementRef}
						defaultViewType={defaultViewType}
						cardType="SAMPLE_VIEW"
					/>
				</div>
				{(sampleInfo?.description?.length > 0 || sampleInfo?.requestedDocumentList?.length > 0) && (
					<div style={{ margin: '12px 24px' }}>
						<TopDetails
							type={'SAMPLE'}
							description={sampleInfo?.description}
							documentLists={sampleInfo?.requestedDocumentList}
						/>
					</div>
				)}
			</>
		);
	};

	const handleCheckbox = (e, value) => {
		if (e.target.checked) {
			setSelectedIds((prevState) => [...prevState, value]);
		} else {
			let ids = selectedIds.filter((item) => parseInt(item.id) !== parseInt(value.id));
			setSelectedIds(ids);
		}
	};

	const isCheckboxChecked = (item) => {
		return selectedIds?.find((value) => parseInt(value.id) === parseInt(item.id));
	};
	const handlePageRoute = () => {
		let path = location.pathname;
		if (location.search) {
			path += `${location.search}`;
		}
		localStorage.setItem('fromRoute', path);
	};

	//country in house doropdown handler
	const handleDropDown = (obj, type) => {
		// dispatch(openLoader());
		// const setIds = getSetMembersIds(item);
		// let postData = {
		// 	id: item.id,
		// 	operationalUnitId: obj.id,
		// 	setMemberIdList: setIds,
		// 	isSet: item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
		// };
		// Http.PUT(`${BASE_URL}/sample/request/${item.id}/items`, postData, '', true)
		// 	.then(() => {
		// 		fetchSampleDetails();
		// 		dispatch(closeLoader());
		// 	})
		// 	.catch((error) => {
		// 		toastError(error.response.data.message);
		// 		dispatch(closeLoader());
		// 	});

		let updatedSelectedRow = {
			...selectedRow,
			[type]: { id: obj.id, name: obj.value }
		};

		if (type === 'developmentFactory' && selectedRow?.fabricType === 'KNIT') {
			updatedSelectedRow = {
				...updatedSelectedRow,
				fabricSupplier: { id: obj.id, name: obj.value }
			};
		}
		if (type === 'unitResponse') {
			updatedSelectedRow = {
				...updatedSelectedRow,
				fabricSupplier: null,
				developmentFactory: null
			};
		}
		setSelectedRow(updatedSelectedRow);
	};

	const handleCloseModal = () => {
		setSelectedIds([]);
	};

	const handleSelectAll = (e) => {
		let tmpIds = [];
		if (e.target.checked) {
			for (let item of listOfSample) {
				if (item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT) {
					for (let setItem of item.setMemberSampleRequestItemResponseList) {
						if (setItem.status !== 'DELIVERED') {
							tmpIds.push(setItem);
						}
					}
				}
				if (
					item.status !== 'DELIVERED' &&
					item.setPackStyleFlag !== STATIC_SET_CONSTANTS.SET_PRODUCT
				) {
					tmpIds.push(item);
				}
			}
		}
		setSelectedIds(tmpIds);
	};

	const onClearChat = () => {
		setListOfSample((prevQuoteStore) => {
			const updatedQuoteStore = prevQuoteStore?.map((item) => {
				if (item.productId === selectedId) {
					return {
						...item,
						noOfUnreadMessage: 0
					};
				}
				return item;
			});
			return updatedQuoteStore;
		});
		setSelectedId(null);
	};

	const handleCommentClose = () => {
		dispatch(clearCurrentActivePage());
		setShowComment(false);
		onClearChat();
	};

	const getTotalDesignsCount = () => {
		let designsCount = 0;
		if (listOfSample && listOfSample.length > 0) {
			for (let design of listOfSample) {
				if (design.setPackStyleFlag !== STATIC_SET_CONSTANTS.SET_PRODUCT) {
					designsCount++;
				}
				if (
					design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
					design.setMemberSampleRequestItemResponseList.length > 0
				) {
					designsCount += design.setMemberSampleRequestItemResponseList.length;
				}
			}
		}
		return designsCount;
	};

	const isDisableSingleSelect = (product) => {
		const isSampleDevelopment = checkPrimaryUserType('SAMPLE_DEVELOPMENT');
		const isDelivered = product.status === 'DELIVERED';
		if (isSampleDevelopment) {
			return isDelivered || ['BD-Factory', 'Turkey-Factory'].includes(product?.unitResponse?.name);
		} else {
			return isDelivered;
		}
	};

	// size and qnt column
	const renderSampleSizeAndQty = (item) => {
		return item?.sampleItemDetailsResponseList?.map((sample) => {
			return (
				<ul className="common-list-component align-items-center mb-2" key={sample.id}>
					{sample?.colorResponse?.hexCode && (
						<li className="d-flex align-items-center">
							<span
								className="color-icon"
								style={{ background: sample?.colorResponse?.hexCode }}
							/>
							<span className="ml-1">{sample?.colorResponse?.code}</span>
						</li>
					)}
					{sample?.colorResponse?.name && (
						<li>
							<span>{sample?.colorResponse?.name}</span>
						</li>
					)}
					<li>
						{sample?.size ? sample?.size + ' - ' : ''}
						{sample?.quantity ? sample?.quantity + ' PCS' : ''}
					</li>
				</ul>
			);
		});
	};

	const renderRoleWiseTableHead = () => {
		if (isPatternMaster() && !isSampleOrProductHead()) {
			return (
				<tr>
					<th>Design ({listOfSample?.length || 0})</th>
					<th>Required &amp; ETD</th>
					<th>Size &amp; qty</th>
					<th>Pattern</th>
					{!isPatternMaster() && <th>Measurement</th>}
					<th>Country In-house/factory</th>
					<th>Notes</th>
					<th>Conversation</th>
				</tr>
			);
		} else if (isSampleOrProductHead()) {
			return (
				<tr>
					<th className="select-box-column">
						<div className="custom-chekbox">
							<div className="form-group m-0">
								<input
									type="checkbox"
									name="Company"
									id="sampleAllSelect"
									checked={
										getTotalDesignsCount() > 0
											? getTotalDesignsCount() === selectedIds?.length
											: null
									}
									onChange={handleSelectAll}
								/>
								<label htmlFor="sampleAllSelect"></label>
							</div>
						</div>
					</th>
					<th>Design ({listOfSample?.length || 0})</th>
					<th className="text-nowrap">Fabric color, Size &amp; qty</th>
					<th>Pattern</th>
					<th>Cutting</th>
					<th>Embellishment</th>
					<th>Sewing</th>
					<th>Consumption</th>
					{/* <th>Country In-house/factory</th> */}
					<th>Development</th>
					<th>Submission Plan Date</th>
					<th>Required Date</th>
					<th className="text-nowrap">Status & date</th>
					<th>Notes</th>
					<th>Conversation</th>
				</tr>
			);
		}
	};
	const renderCheckBox = (product) => {
		return (
			<div
				className={
					isDisableSingleSelect(product)
						? 'custom-chekbox d-inline-block disabled align-middle'
						: 'custom-chekbox d-inline-block align-middle'
				}
			>
				<div className="form-group m-0">
					<input
						type="checkbox"
						id={`sample_checkbox${product?.id}`}
						name="Company"
						disabled={isDisableSingleSelect(product)}
						value={product?.id}
						checked={isCheckboxChecked(product)}
						onChange={(e) => handleCheckbox(e, product)}
					/>
					<label htmlFor={`sample_checkbox${product?.id}`} />
				</div>
			</div>
		);
	};

	const renderDesignDetails = (design) => {
		return (
			<div className="ml-12">
				<div>
					{design?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && setTag()}
					{design?.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT && packTag()}
				</div>
				<p className="regular-12 gray_dark_01 py-1">{design?.referenceNumber}</p>
				<ul className="common-list-component mb-2">
					{design?.market && <li>{design?.market}</li>}
					{!design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && design.category && (
						<li>{design?.category} </li>
					)}
					{design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
						design.setMemberSampleRequestItemResponseList &&
						design?.setMemberSampleRequestItemResponseList.map((member) => (
							<li key={member?.id}>{member?.category} </li>
						))}
				</ul>
				<ul className="common-list-component mb-2">
					{design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
					design?.setMemberSampleRequestItemResponseList?.length > 0
						? renderProductCreator(design?.setMemberSampleRequestItemResponseList[0], true)
						: renderProductCreator(
								design,
								false,
								design.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT
							)}
				</ul>
			</div>
		);
	};

	const renderPatternDetails = (product) => {
		return (
			<div>
				<p className="regular-12 gray_dark_01">{product?.patternActivityResponse?.assignedToName}</p>
				{product?.patternActivityResponse?.requiredDate && (
					<p className="regular-12 primary_bright_02">
						{changeDateFormat(
							product?.patternActivityResponse?.requiredDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
					</p>
				)}
				<p className="regular-12 green">
					{product?.patternActivityResponse?.deliveryDate &&
						changeDateFormat(
							product?.patternActivityResponse?.deliveryDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
					{product?.patternActivityResponse?.status === 'COMPLETED' && (
						<img
							src={`${process.env.PUBLIC_URL}/icons/check.svg`}
							alt="done"
							className="checked"
						/>
					)}
					{product?.isMeasurementCompleted && !checkPrimaryUserType('PATTERN_MASTER') && (
						<img
							src={`${process.env.PUBLIC_URL}/icons/check.svg`}
							alt="done"
							className="checked"
						/>
					)}
				</p>
				{!product?.patternActivityResponse?.deliveryDate &&
					product?.patternActivityResponse?.assignedTo && (
						<div className="hover-btn">
							{/* {isSampleOrProductHead() && !product?.isMeasurementCompleted ? (
								<button
									onClick={() => {
										handlePageRoute();
										navigate(
											`/designs/edit/${product?.productId}?activeTab=measurement&requestFrom=sample`
										);
									}}
									className="button size32"
								>
									Measurement
								</button>
							) : ( */}
							<button
								onClick={() => handleComplete('Pattern', product?.id)}
								className="button approve size32"
							>
								Complete
							</button>
							{/* )} */}
						</div>
					)}
			</div>
		);
	};

	const renderCuttingDetails = (product) => {
		return (
			<div>
				<p className="regular-12 gray_dark_01">{product?.cuttingActivityResponse?.assignedToName}</p>
				{product?.cuttingActivityResponse?.requiredDate && (
					<p className="regular-12 primary_bright_02">
						{changeDateFormat(
							product?.cuttingActivityResponse?.requiredDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
					</p>
				)}
				{product?.cuttingActivityResponse?.deliveryDate && (
					<p className="regular-12 green">
						{changeDateFormat(
							product?.cuttingActivityResponse?.deliveryDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
						<img
							src={`${process.env.PUBLIC_URL}/icons/check.svg`}
							alt="done"
							className="checked"
						/>
					</p>
				)}
				{product?.cuttingActivityResponse?.status === 'PENDING' && (
					<div className="hover-btn">
						<button
							onClick={() =>
								handleComplete(product?.cuttingActivityResponse?.activityType, product?.id)
							}
							className="button approve size32"
						>
							Complete
						</button>
					</div>
				)}
			</div>
		);
	};

	const renderEmbellishmentDetails = (product) => {
		return (
			<div>
				<p className="regular-12 gray_dark_01">
					<span className="sample-view-embellishment-status">
						{product?.embellishmentActivityResponse?.activityType}
					</span>
				</p>
				{product?.embellishmentActivityResponse?.createdAt && (
					<p className="regular-12 primary_bright_02">
						{changeDateFormat(
							product?.embellishmentActivityResponse?.createdAt,
							'YYYY-MM-DD',
							'DD MMM'
						)}
					</p>
				)}
				{product?.embellishmentActivityResponse?.deliveryDate && (
					<p className="regular-12 green">
						{changeDateFormat(
							product?.embellishmentActivityResponse?.deliveryDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
						<img
							src={`${process.env.PUBLIC_URL}/icons/check.svg`}
							alt="done"
							className="checked"
						/>
					</p>
				)}
				{product?.embellishmentActivityResponse?.status === 'PENDING' && (
					<div className="hover-btn">
						<button
							onClick={() =>
								handleComplete(
									product?.embellishmentActivityResponse?.activityType,
									product?.id
								)
							}
							className="button approve size32"
						>
							Complete
						</button>
					</div>
				)}
			</div>
		);
	};

	const renderSwingColumn = (product) => {
		return (
			<div>
				<p className="regular-12 gray_dark_01">{product?.sewingActivityResponse?.assignedToName}</p>
				{product?.sewingActivityResponse?.requiredDate && (
					<p className="regular-12 primary_bright_02">
						{changeDateFormat(
							product?.sewingActivityResponse?.requiredDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
					</p>
				)}
				{product?.sewingActivityResponse?.deliveryDate && (
					<p className="regular-12 green">
						{changeDateFormat(
							product?.sewingActivityResponse?.deliveryDate,
							'YYYY-MM-DD',
							'DD MMM'
						)}
						<img
							src={`${process.env.PUBLIC_URL}/icons/check.svg`}
							alt="done"
							className="checked"
						/>
					</p>
				)}
				{product?.sewingActivityResponse?.status === 'PENDING' && (
					<div className="hover-btn">
						<button
							onClick={() =>
								handleComplete(product?.sewingActivityResponse?.activityType, product?.id)
							}
							className="button approve size32"
						>
							Complete
						</button>
					</div>
				)}
			</div>
		);
	};
	const renderConsumptionDetails = (product) => {
		return (
			<div>
				{!product?.isConsumptionCompleted && (
					<div className="hover-btn">
						<button
							onClick={() => {
								handlePageRoute();
								navigate(`/designs/edit/${product?.productId}?activeTab=materials`);
							}}
							className="button size32"
						>
							Material
						</button>
					</div>
				)}
				{product?.isConsumptionCompleted && (
					<img src={`${process.env.PUBLIC_URL}/icons/check.svg`} alt="done" className="checked" />
				)}
			</div>
		);
	};
	const renderRequiredETD = (product) => {
		return (
			<div>
				{product?.requireDate && (
					<p className="regular-14 gray_dark_02">
						<span className="gray_dark_01">
							{changeDateFormat(product?.requireDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}
						</span>
					</p>
				)}
			</div>
		);
	};

	const renderSetItemTableRow = (setProducts = []) => {
		const skipOneSetDesign = setProducts.slice(1);
		return (
			<>
				{skipOneSetDesign.map((design) => (
					<tr key={design.id}>
						<td
							className={
								isCheckboxChecked(design) ? 'checked select-box-column' : 'select-box-column'
							}
						>
							{renderCheckBox(design)}
						</td>
						<td>{renderSampleSizeAndQty(design)}</td>
						<td>{renderPatternDetails(design)}</td>
						<td>{renderCuttingDetails(design)}</td>
						<td>{renderEmbellishmentDetails(design)}</td>
						<td>{renderSwingColumn(design)}</td>
						<td>{renderConsumptionDetails(design)}</td>
					</tr>
				))}
			</>
		);
	};

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const filterCountryUnits = (unitName) => {
		let countryPrefix = unitName?.trim('-')[0];
		return countryList.filter((item) => item.value.startsWith(countryPrefix));
	};

	const onFetchSupplierList = async (value) => {
		try {
			dispatch(openLoader());
			let response = await fetchSupplierByMaterialType(value);
			response = response?.data?.map((item) => {
				return {
					id: item.id,
					value: item.name
				};
			});
			setSupplierList(response);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderDevelopmentDetails = (developmentDetails) => {
		return (
			<div className="sample-status-with-details">
				<div className="d-flex align-items-center">
					<p className="regular-14 weight-300 gray_dark_02">Country In-house/Factory: </p>
					<Tooltip
						title={<span className="regular-14">{developmentDetails?.unitResponse?.name}</span>}
						placement="top"
						arrow
					>
						<span className="gray_dark_01 ml-1">
							{getShortName(developmentDetails?.unitResponse?.name, 12)}
						</span>
					</Tooltip>
				</div>

				<div className="d-flex align-items-center">
					<p className="regular-14 weight-300 gray_dark_02">Development Factory: </p>
					<Tooltip
						title={
							<span className="regular-14">{developmentDetails?.developmentFactory?.name}</span>
						}
						placement="top"
						arrow
					>
						<span className="gray_dark_01 ml-1">
							{' '}
							{getShortName(developmentDetails?.developmentFactory?.name, 17)}
						</span>
					</Tooltip>
				</div>

				<div className="d-flex align-items-center">
					<p className="regular-14 weight-300 gray_dark_02">Fabric Supplier: </p>
					<Tooltip
						title={<span className="regular-14">{developmentDetails?.fabricSupplier?.name}</span>}
						placement="top"
						arrow
					>
						<span className="gray_dark_01 ml-1">
							{getShortName(developmentDetails?.fabricSupplier?.name, 23)}
						</span>
					</Tooltip>
				</div>
				{checkPrimaryUserType('PRODUCT_DEVELOPMENT') && (
					<button
						className="button text-only"
						onClick={() => {
							onFetchSupplierList();
							setSelectedRow(developmentDetails);
							setIsUpdateClick(true);
						}}
					>
						<span className="blue weight-500">Update</span>
					</button>
				)}
			</div>
		);
	};

	const getRowSpan = (item) =>
		(item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
			item?.setMemberSampleRequestItemResponseList?.length) ||
		1;

	const renderSatusColumn = (statusDetails) => {
		return (
			<div className="sample-status-with-details">
				<div className="sample-status">
					{getColorWiseActivityStatusOfSample(
						statusDetails?.currentActivity?.activityType === 'REQUESTED'
							? 'SAMPLE_REQUESTED'
							: statusDetails?.currentActivity?.activityType,
						getActionDoneBy(statusDetails?.currentActivity?.userType || null)
					)}
					{statusDetails.basePrice && (
						<p className="costing-amount regular-14">
							${statusDetails.basePrice?.toFixed(2)} <span>/unit</span>
						</p>
					)}
					{statusDetails?.consumptionResponse &&
						statusDetails.consumptionResponse.map((res) => (
							<ul className="common-list-component" key={_getKey()}>
								<li>
									{res.quantity}
									{res.quantityUnit}
								</li>
							</ul>
						))}
				</div>
				<p>{changeDateFormat(statusDetails?.currentActivity?.createdAt, '', 'Do MMM, YYYY')}</p>
				{statusDetails.id && (
					<RequestTimeline
						requireDate={statusDetails?.requireDate}
						currentActivity={statusDetails?.currentActivity?.activityType || null}
						activityList={statusDetails.timeLineActivityResponse}
					/>
				)}
			</div>
		);
	};

	const renderRoleWiseTableBody = () => {
		return listOfSample?.map((item) => {
			if (isPatternMaster() && !isSampleOrProductHead()) {
				return (
					<TableBodyForPatternMaster
						setDesignIdToView={setDesignIdToView}
						setActiveRemarksId={setActiveRemarksId}
						item={item}
						handlePageRoute={handlePageRoute}
						activeRemarksId={activeRemarksId}
						setShowComment={setShowComment}
						countryList={countryList}
						setCurrentActivePage={setCurrentActivePage}
						setSelectedId={setSelectedId}
						id={id}
						handleComplete={handleComplete}
						renderSampleSizeAndQty={renderSampleSizeAndQty}
						key={item.id}
					/>
				);
			} else if (isSampleOrProductHead()) {
				return (
					<React.Fragment key={`sample_request_item_${item.id}`}>
						<tr className={selectedSampleItemId == item.id ? 'highlight-table-row' : ''}>
							<td
								className={
									isCheckboxChecked(
										item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
											? item?.setMemberSampleRequestItemResponseList[0]
											: item
									)
										? 'checked select-box-column'
										: 'select-box-column'
								}
							>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderCheckBox(item?.setMemberSampleRequestItemResponseList[0])
									: renderCheckBox(item)}
							</td>
							<td
								className="design-column"
								rowSpan={
									(item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
										item?.setMemberSampleRequestItemResponseList?.length) ||
									1
								}
							>
								<div
									onClick={() => openDesignViewModal(item, item?.productId)}
									data-toggle="modal"
									data-target="#designViewModal"
									className="sample-details-with-image d-flex align-items-center"
								>
									{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT ? (
										<div className="set-image-layout" style={{ width: ' 68px' }}>
											<img
												src={
													item?.setMemberSampleRequestItemResponseList[0]
														?.featureImageDocUrl
												}
												alt="image"
												className="cell-img pr-2"
											/>
											<img
												src={
													item?.setMemberSampleRequestItemResponseList[1]
														?.featureImageDocUrl
												}
												alt="image"
												className="cell-img pr-2"
											/>
										</div>
									) : (
										<img
											src={item?.featureImageDocUrl}
											alt="image"
											className="cell-img pr-2"
										/>
									)}
									{renderDesignDetails(item, false, [])}
								</div>
							</td>
							<td>
								<div className="sample-details">
									{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
										? renderSampleSizeAndQty(
												item?.setMemberSampleRequestItemResponseList[0]
											)
										: renderSampleSizeAndQty(item)}
								</div>
							</td>
							<td>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderPatternDetails(item?.setMemberSampleRequestItemResponseList[0])
									: renderPatternDetails(item)}
							</td>
							<td>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderCuttingDetails(item?.setMemberSampleRequestItemResponseList[0])
									: renderCuttingDetails(item)}
							</td>
							<td>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderEmbellishmentDetails(
											item?.setMemberSampleRequestItemResponseList[0]
										)
									: renderEmbellishmentDetails(item)}
							</td>
							<td>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderSwingColumn(item?.setMemberSampleRequestItemResponseList[0])
									: renderSwingColumn(item)}
							</td>
							<td>
								{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
									? renderConsumptionDetails(
											item?.setMemberSampleRequestItemResponseList[0]
										)
									: renderConsumptionDetails(item)}
							</td>

							{/* development column */}
							<td rowSpan={getRowSpan(item)} className="development-column">
								<div>{renderDevelopmentDetails(item)}</div>
							</td>

							<td rowSpan={getRowSpan(item)}>
								<input
									type="date"
									name="submission-date"
									value={item?.submissionPlanDate}
									onChange={(e) => onChangeSubmissionDate(e, item)}
									onClick={() => setSelectedRow(item)}
									disabled={!checkPrimaryUserType('PRODUCT_DEVELOPMENT')}
								/>
							</td>

							<td
								className="required-etd-column"
								rowSpan={
									(item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
										item?.setMemberSampleRequestItemResponseList.length) ||
									1
								}
							>
								{renderRequiredETD(item)}
							</td>

							{/* Status Column  */}
							<td rowSpan={getRowSpan(item)} className="status-column">
								{renderSatusColumn(item)}
							</td>

							<td
								onClick={() => setActiveRemarksId(item?.id)}
								rowSpan={
									(item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
										item?.setMemberSampleRequestItemResponseList.length) ||
									1
								}
							>
								<SampleInlineComment
									sampleRequestId={id}
									id={item?.id}
									remarks={item?.remarks}
									activeRemarksId={activeRemarksId}
									onReset={() => {
										setActiveRemarksId(null);
									}}
									isSet={item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT}
									setmembers={item?.setMemberSampleRequestItemResponseList}
								/>
							</td>
							<td
								rowSpan={
									(item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
										item?.setMemberSampleRequestItemResponseList.length) ||
									1
								}
							>
								<div className="col-md-1 one-sixth text-right">
									<button
										className="button text cursor-pointer"
										onClick={() => {
											dispatch(
												setCurrentActivePage({
													id: item?.productId,
													type: 'PRODUCT_CONVERSATION',
													pageType: 'DETAILS'
												})
											);
											setSelectedId(item?.productId);
											setShowComment(true);
										}}
									>
										{item?.noOfUnreadMessage > 0 ? (
											<img
												src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
												alt="not read"
											/>
										) : (
											<img
												src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
												alt="not read"
											/>
										)}
									</button>
								</div>
							</td>
						</tr>
						{item.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
							renderSetItemTableRow(item?.setMemberSampleRequestItemResponseList)}
					</React.Fragment>
				);
			}
		});
	};

	const onCollspseFilter = () => setIsShowFilter(!isShowFilter);

	return (
		<>
			<div>
				{sampleInfo && renderTopBar()}

				<section className="filter-data-container filter-v3-data-container">
					<FilterComponent
						filterPageName={allFilterPages.SAMPLE_DETAILS}
						fields={sampleDetailsFilterFields}
						showFilter={isShowFilter}
						isDependentFilter={true}
					/>
					<div className="data-table w-100 overflow-hidden">
						<div className="sample-dashboard-container no-tab-bar">
							<div className="sample-request-gorup">
								<div className="sample-details-view-table pattern-master-view-table">
									{defaultViewType === LIST_VIEW_TYPE.LIST_VIEW && (
										<table
											className="table with-border no-hover scroll-x-label scroll-y-label"
											ref={elementRef}
										>
											<thead>{renderRoleWiseTableHead()}</thead>
											<tbody>{renderRoleWiseTableBody()}</tbody>
										</table>
									)}
									{defaultViewType !== LIST_VIEW_TYPE.LIST_VIEW && (
										<SampleDesignCard
											sampleList={listOfSample}
											sampleInfo={sampleInfo}
											selectedIds={selectedIds}
											setSelectedIds={setSelectedIds}
											openDesignViewModal={openDesignViewModal}
											isCheckboxChecked={isCheckboxChecked}
										/>
									)}
								</div>
							</div>
							{selectedIds.length > 0 && (
								<SampleActionPopup selectedIds={selectedIds} closeModal={handleCloseModal} />
							)}

							{isUpdateClick && (
								<ModalContainer className="width-784-dialog development-info-modal">
									<div className="common-popup shadow-24dp">
										<div className="common-popup-header d-flex justify-content-between">
											<div className="popup-tilte">
												<h3 className="semibold-16 mb-0">Development Info</h3>
											</div>
											<div
												className="close-btn"
												onClick={() => {
													setSelectedRow(null);
													setIsUpdateClick(false);
												}}
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/close.svg`}
													alt="close"
												/>
											</div>
										</div>
										<div className="common-popup-body">
											<table className="table with-border no-hover">
												<thead>
													<tr>
														<th>Country In-house/Factory</th>
														<th>Development Factory</th>
														<th>Fabric Supplier</th>
													</tr>
												</thead>

												<tbody>
													<tr>
														<td>
															<div className="transparent-select">
																<CustomDropdown
																	type="addItem"
																	title={'Country'}
																	onItemClick={(obj) =>
																		handleDropDown(obj, 'unitResponse')
																	}
																	items={filterCountryUnits(
																		selectedRow?.unitResponse?.name
																	)}
																	selectedItem={
																		selectedRow?.unitResponse?.name
																	}
																	toolTipLength={25}
																/>
															</div>
														</td>
														<td
															className={`${
																isDisabledSelect ? 'disabled' : ''
															}`}
														>
															<div className="transparent-select">
																<CustomDropdown
																	onItemSearch={(searchTerm) =>
																		onFetchSupplierList(searchTerm)
																	}
																	onItemClick={(obj) =>
																		handleDropDown(
																			obj,
																			'developmentFactory'
																		)
																	}
																	items={supplierList}
																	selectedItem={
																		selectedRow?.developmentFactory?.name
																	}
																	toolTipLength={25}
																	isDisabled={isDisabledSelect}
																/>
															</div>
														</td>
														<td
															className={`${
																isDisabledSelect ? 'disabled' : ''
															}`}
														>
															<div className="transparent-select">
																<CustomDropdown
																	title={'Select'}
																	onItemSearch={(searchTerm) =>
																		onFetchSupplierList(searchTerm)
																	}
																	onItemClick={(obj) =>
																		handleDropDown(obj, 'fabricSupplier')
																	}
																	items={supplierList}
																	selectedItem={
																		selectedRow?.fabricSupplier?.name
																	}
																	toolTipLength={25}
																	isDisabled={isDisabledSelect}
																/>
															</div>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
										<div className="modal-footer">
											<div className="footer-buttons text-right">
												<button
													className="button gray-outline size32"
													onClick={() => {
														setSelectedRow(null);
														setIsUpdateClick(false);
													}}
												>
													Cancel
												</button>
												<button
													className="button size32 ml-2"
													disabled={isLoading}
													onClick={() => handleSubmit(selectedRow)}
												>
													Update
												</button>
											</div>
										</div>
									</div>
								</ModalContainer>
							)}
						</div>
					</div>
				</section>
			</div>
			{selectedId && (
				<SampleComment
					sampleRequestId={id}
					postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
					topic={'PRODUCT_SAMPLE_COMMENT'}
					postTypeId={'sampleRequestId'}
					productId={selectedId}
					showComment={showComment}
					setShowComment={handleCommentClose}
					brandInfo={{
						brandId: sampleViewStore?.brandId,
						brandName: sampleViewStore?.brand
					}}
				/>
			)}
		</>
	);
};

export default SampleViewForSampleDepartment;
